// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/** Output delivery: download, Save as…, clipboard, Web Share. All local. */

/** Trigger a plain browser download via an object URL. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

interface SaveFilePickerWindow {
  showSaveFilePicker?: (opts: {
    suggestedName?: string;
    types?: Array<{ description?: string; accept: Record<string, string[]> }>;
  }) => Promise<{
    createWritable: () => Promise<{
      write: (data: Blob) => Promise<void>;
      close: () => Promise<void>;
    }>;
  }>;
}

export function canSaveFilePicker(): boolean {
  return typeof (window as unknown as SaveFilePickerWindow).showSaveFilePicker === 'function';
}

/**
 * Save via the File System Access API when available, falling back to a normal
 * download. Returns 'saved' | 'downloaded' | 'cancelled'.
 */
export async function saveBlob(blob: Blob, filename: string): Promise<'saved' | 'downloaded' | 'cancelled'> {
  const w = window as unknown as SaveFilePickerWindow;
  if (typeof w.showSaveFilePicker === 'function') {
    try {
      const handle = await w.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'PNG image', accept: { 'image/png': ['.png'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return 'saved';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
      // Any other failure → fall back to a download.
    }
  }
  downloadBlob(blob, filename);
  return 'downloaded';
}

export function canCopyImage(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard &&
    typeof (window as { ClipboardItem?: unknown }).ClipboardItem !== 'undefined'
  );
}

export async function copyImage(blob: Blob): Promise<void> {
  const CI = (window as unknown as { ClipboardItem: typeof ClipboardItem }).ClipboardItem;
  await navigator.clipboard.write([new CI({ [blob.type || 'image/png']: blob })]);
}

export function canShareFiles(files: File[]): boolean {
  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  return typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files });
}

export async function shareFile(blob: Blob, filename: string, title: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type || 'image/png' });
  await navigator.share({ files: [file], title });
}
